import React from 'react'
import { LucideIcon, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { cn } from '../../utils/cn'
import type { BirhausStatCardProps, StatTrend, StatColor, StatMetrics } from './types'

/**
 * BirhausStatCard - Statistics display component with BIRHAUS principles
 * 
 * Features:
 * - Spanish-first labels with English fallbacks
 * - Cognitive load reduction through clear visual hierarchy
 * - Accessibility-first design (WCAG AA+)
 * - Consistent color scheme following BIRHAUS design tokens
 * - Support for trend indicators and contextual information
 */

// BIRHAUS color configurations
const colorClasses = {
  blue: {
    bg: 'bg-blue-50',
    icon: 'text-blue-600',
    trend: 'text-blue-600',
    ring: 'ring-blue-200'
  },
  green: {
    bg: 'bg-emerald-50',
    icon: 'text-emerald-600',
    trend: 'text-emerald-600',
    ring: 'ring-emerald-200'
  },
  purple: {
    bg: 'bg-purple-50',
    icon: 'text-purple-600',
    trend: 'text-purple-600',
    ring: 'ring-purple-200'
  },
  indigo: {
    bg: 'bg-indigo-50',
    icon: 'text-indigo-600',
    trend: 'text-indigo-600',
    ring: 'ring-indigo-200'
  },
  amber: {
    bg: 'bg-amber-50',
    icon: 'text-amber-600',
    trend: 'text-amber-600',
    ring: 'ring-amber-200'
  },
  red: {
    bg: 'bg-red-50',
    icon: 'text-red-600',
    trend: 'text-red-600',
    ring: 'ring-red-200'
  },
  gray: {
    bg: 'bg-gray-50',
    icon: 'text-gray-600',
    trend: 'text-gray-600',
    ring: 'ring-gray-200'
  },
  teal: {
    bg: 'bg-teal-50',
    icon: 'text-teal-600',
    trend: 'text-teal-600',
    ring: 'ring-teal-200'
  }
}

export function BirhausStatCard({
  // Labels - BIRHAUS Spanish-first
  tituloEs,
  tituloEn,
  subtituloEs,
  subtituloEn,
  
  // Data
  value,
  
  // Trend information
  trend,
  trendValue,
  trendLabelEs,
  trendLabelEn,
  
  // Visual configuration
  icon: Icon,
  color = 'blue',
  variant = 'default',
  size = 'default',
  
  // Interaction
  onClick,
  loading = false,
  
  // Accessibility & localization
  className,
  locale = 'es',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy
}: BirhausStatCardProps) {
  
  // BIRHAUS Spanish-first label resolution
  const getLabel = (es: string, en?: string) => {
    return locale === 'es' ? es : (en || es)
  }
  
  const colors = colorClasses[color]
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus
  
  // Size variants following BIRHAUS 8pt grid
  const sizeClasses = {
    compact: 'p-4',
    default: 'p-6',
    large: 'p-8'
  }
  
  const titleSizeClasses = {
    compact: 'text-sm',
    default: 'text-sm',
    large: 'text-base'
  }
  
  const valueSizeClasses = {
    compact: 'text-xl',
    default: 'text-2xl',
    large: 'text-3xl'
  }
  
  // Variant classes
  const variantClasses = {
    default: 'bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow',
    elevated: 'bg-white border border-gray-200 shadow-lg hover:shadow-xl transition-shadow',
    outlined: 'bg-transparent border-2 border-gray-300 hover:border-gray-400 transition-colors',
    minimal: 'bg-transparent border-0'
  }
  
  const displayTitle = getLabel(tituloEs, tituloEn)
  const displaySubtitle = subtituloEs || subtituloEn ? getLabel(subtituloEs!, subtituloEn) : undefined
  const displayTrendLabel = trendLabelEs || trendLabelEn ? getLabel(trendLabelEs!, trendLabelEn) : undefined
  
  // Format value if it's a number
  const formatValue = (val: string | number): string => {
    if (typeof val === 'number') {
      // Use Spanish formatting for numbers
      return new Intl.NumberFormat(locale === 'es' ? 'es-ES' : 'en-US').format(val)
    }
    return String(val)
  }
  
  const cardContent = (
    <div className={cn(
      'rounded-lg transition-all duration-200',
      variantClasses[variant],
      sizeClasses[size],
      onClick && 'cursor-pointer hover:ring-2',
      onClick && colors.ring,
      loading && 'animate-pulse',
      className
    )}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          {/* Title */}
          <p className={cn(
            'font-medium text-gray-600 truncate',
            titleSizeClasses[size]
          )}>
            {displayTitle}
          </p>
          
          {/* Value */}
          <div className="flex items-baseline mt-1">
            {loading ? (
              <div className="h-8 w-24 bg-gray-200 rounded animate-pulse" />
            ) : (
              <p className={cn(
                'font-bold text-gray-900',
                valueSizeClasses[size]
              )}>
                {formatValue(value)}
              </p>
            )}
          </div>
          
          {/* Subtitle */}
          {displaySubtitle && (
            <p className="text-xs text-gray-500 mt-1 truncate">
              {displaySubtitle}
            </p>
          )}
          
          {/* Trend */}
          {trend && (
            <div className={cn(
              'flex items-center mt-2 text-sm',
              colors.trend
            )}>
              <TrendIcon className="w-4 h-4 mr-1" />
              {trendValue && (
                <span className="font-medium mr-1">{trendValue}</span>
              )}
              {displayTrendLabel && (
                <span className="text-gray-600">{displayTrendLabel}</span>
              )}
            </div>
          )}
        </div>
        
        {/* Icon */}
        {Icon && (
          <div className={cn(
            'flex-shrink-0 p-3 rounded-lg ml-4',
            colors.bg
          )}>
            <Icon className={cn(
              'w-6 h-6',
              colors.icon
            )} />
          </div>
        )}
      </div>
    </div>
  )
  
  // Wrap in button if clickable, otherwise return as div
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
        aria-label={ariaLabel || displayTitle}
        aria-describedby={ariaDescribedBy}
      >
        {cardContent}
      </button>
    )
  }
  
  return (
    <div 
      role="img"
      aria-label={ariaLabel || displayTitle}
      aria-describedby={ariaDescribedBy}
    >
      {cardContent}
    </div>
  )
}

// Helper function to create stat metrics for BIRHAUS compliance
export function createStatMetrics(
  value: number,
  previousValue?: number,
  target?: number
): StatMetrics {
  const metrics: StatMetrics = {
    value,
    formatted: new Intl.NumberFormat('es-ES').format(value),
    trend: null,
    trendPercentage: null,
    targetProgress: null
  }
  
  // Calculate trend if previous value is provided
  if (previousValue !== undefined && previousValue !== 0) {
    const change = value - previousValue
    const percentageChange = (change / previousValue) * 100
    
    metrics.trend = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral'
    metrics.trendPercentage = Math.abs(percentageChange)
  }
  
  // Calculate target progress if target is provided
  if (target !== undefined && target > 0) {
    metrics.targetProgress = Math.min((value / target) * 100, 100)
  }
  
  return metrics
}

// Pre-configured stat card variants for common use cases
export const birhausStatCardPresets = {
  // Financial metrics
  revenue: {
    color: 'green' as StatColor,
    tituloEs: 'Ingresos',
    tituloEn: 'Revenue'
  },
  
  expenses: {
    color: 'red' as StatColor,
    tituloEs: 'Gastos',
    tituloEn: 'Expenses'
  },
  
  profit: {
    color: 'blue' as StatColor,
    tituloEs: 'Ganancia',
    tituloEn: 'Profit'
  },
  
  // Student metrics
  totalStudents: {
    color: 'indigo' as StatColor,
    tituloEs: 'Total de Estudiantes',
    tituloEn: 'Total Students'
  },
  
  activeStudents: {
    color: 'green' as StatColor,
    tituloEs: 'Estudiantes Activos',
    tituloEn: 'Active Students'
  },
  
  newEnrollments: {
    color: 'purple' as StatColor,
    tituloEs: 'Nuevas Inscripciones',
    tituloEn: 'New Enrollments'
  },
  
  // Course metrics
  totalCourses: {
    color: 'teal' as StatColor,
    tituloEs: 'Total de Cursos',
    tituloEn: 'Total Courses'
  },
  
  activeCourses: {
    color: 'blue' as StatColor,
    tituloEs: 'Cursos Activos',
    tituloEn: 'Active Courses'
  },
  
  // Performance metrics
  attendance: {
    color: 'green' as StatColor,
    tituloEs: 'Asistencia',
    tituloEn: 'Attendance'
  },
  
  completion: {
    color: 'purple' as StatColor,
    tituloEs: 'Finalización',
    tituloEn: 'Completion'
  }
} as const

// Hook for managing stat card state with BIRHAUS patterns
export function useBirhausStatCard(initialValue: number = 0, refreshInterval?: number) {
  const [value, setValue] = React.useState(initialValue)
  const [previousValue, setPreviousValue] = React.useState<number | undefined>(undefined)
  const [loading, setLoading] = React.useState(false)
  const [lastUpdated, setLastUpdated] = React.useState<Date>(new Date())
  
  const updateValue = React.useCallback((newValue: number) => {
    setPreviousValue(value)
    setValue(newValue)
    setLastUpdated(new Date())
  }, [value])
  
  const refresh = React.useCallback(() => {
    setLoading(true)
    // Simulate refresh delay
    setTimeout(() => {
      setLoading(false)
      setLastUpdated(new Date())
    }, 500)
  }, [])
  
  // Auto-refresh if interval is provided
  React.useEffect(() => {
    if (refreshInterval && refreshInterval > 0) {
      const interval = setInterval(refresh, refreshInterval)
      return () => clearInterval(interval)
    }
  }, [refreshInterval, refresh])
  
  const metrics = React.useMemo(() => {
    return createStatMetrics(value, previousValue)
  }, [value, previousValue])
  
  return {
    value,
    setValue: updateValue,
    previousValue,
    loading,
    setLoading,
    lastUpdated,
    refresh,
    metrics
  }
}

export { BirhausStatCard as default }